// src/services/shopService.js
//
// SBL SHOP — the browser side. Buying and wearing go through the Cloud
// Functions in functions/shop.js (the browser never sends a price and can't
// write what it owns). This file reads the catalogue, what you own, and
// what you're wearing, all live.

import { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";
import { SEASON_SHOP_ITEMS } from "../components/avatar/seasonItems";

/* ---------- the catalogue (shared by every avatar on the page) ---------- */

const catalog = { items: [], byId: {}, loaded: false, error: "" };
const listeners = new Set();
let stopCatalog = null;

const startCatalog = () => {
  if (stopCatalog) return;
  stopCatalog = onSnapshot(
    collection(db, "shopItems"),
    (snap) => {
      // + the built-in Seasons hats, wings, pets and frames (priced by the
      // server from seasonCatalog.json — nothing to add in the admin panel)
      const items = [...snap.docs.map((d) => ({ id: d.id, ...d.data() })), ...SEASON_SHOP_ITEMS.map((i) => ({ ...i, builtInSeason: true }))]
        .sort((a, b) => (a.kind || "").localeCompare(b.kind || "") || (Number(a.sort) || 0) - (Number(b.sort) || 0) || (Number(a.price) || 0) - (Number(b.price) || 0));
      catalog.items = items;
      catalog.byId = Object.fromEntries(items.map((i) => [i.id, i]));
      catalog.loaded = true;
      catalog.error = "";
      listeners.forEach((fn) => fn({ ...catalog }));
    },
    (err) => {
      catalog.loaded = true;
      catalog.error = err?.code || err?.message || "error";
      listeners.forEach((fn) => fn({ ...catalog }));
    }
  );
};

/** { items, byId, loaded, error } — live. Pass false to skip (items given). */
export const useShopItems = (enabled = true) => {
  const [state, setState] = useState({ ...catalog });
  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(setState);
    startCatalog();
    setState({ ...catalog });
    return () => listeners.delete(setState);
  }, [enabled]);
  return state;
};

/* ---------- what I own and what I'm wearing ---------- */

/**
 * { owned: Set(itemId), qty: { itemId: n }, outfit: { slot: itemId }, loaded }
 * — live. `owned` holds wearables you have; power-ups are counted in `qty`.
 */
export const useMyCloset = () => {
  const [owned, setOwned] = useState(new Set());
  const [qty, setQty] = useState({});
  const [outfit, setOutfit] = useState({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let stops = [];
    const stopAll = () => {
      stops.forEach((s) => s());
      stops = [];
    };
    const stopAuth = onAuthStateChanged(getAuth(), (user) => {
      stopAll();
      if (!user) {
        setOwned(new Set());
        setOutfit({});
        setLoaded(true);
        return;
      }
      stops.push(
        onSnapshot(
          collection(db, "wallets", user.uid, "inventory"),
          (snap) => {
            const q = {};
            const own = new Set();
            snap.docs.forEach((d) => {
              const data = d.data() || {};
              if (data.kind === "powerup") q[d.id] = Number(data.qty) || 0;
              else own.add(d.id);
            });
            setOwned(own);
            setQty(q);
            setLoaded(true);
          },
          () => setLoaded(true)
        )
      );
      stops.push(
        onSnapshot(
          doc(db, "wallets", user.uid),
          (snap) => setOutfit((snap.exists() && snap.data().outfit) || {}),
          () => {}
        )
      );
    });
    // a chest just gave a power-up or an avatar item: show it now
    const onGot = (e) => {
      const { powerup, item } = e.detail || {};
      if (powerup) setQty((q) => ({ ...q, [powerup]: (Number(q[powerup]) || 0) + 1 }));
      if (item) setOwned((o) => new Set([...o, item]));
    };
    window.addEventListener("sbl:rewards-changed", onGot);
    return () => {
      stopAll();
      stopAuth();
      window.removeEventListener("sbl:rewards-changed", onGot);
    };
  }, []);
  return useMemo(() => ({ owned, qty, outfit, loaded }), [owned, qty, outfit, loaded]);
};

/* ---------- actions ---------- */

const call = async (name, data) => {
  try {
    const res = await httpsCallable(functions, name)(data);
    return res.data;
  } catch (err) {
    const code = String(err?.code || "");
    if (code.includes("not-found") && /function|not found/i.test(err?.message || "") && !/shop/i.test(err?.message || "")) {
      throw new Error("The shop isn't switched on yet (its functions aren't deployed).");
    }
    throw new Error(err?.message || String(err));
  }
};

/** Buy (or, for a free item, get) one item. Resolves { coins, price, already }. */
export const buyShopItem = (itemId) => call("buyShopItem", { itemId });

/** Wear an item you own. */
export const wearShopItem = (itemId) => call("equipShopItem", { itemId });

/** Use up one power-up. Resolves { ok, qty } — apply the effect only after. */
export const spendPowerup = (itemId) => call("usePowerup", { itemId });

/** Take off whatever is in a slot. */
export const takeOffSlot = (slot) => call("equipShopItem", { slot, itemId: null });
